// Interactive sub-sections used by SectionView (waitlist form + live countdown).
import { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

export function WaitlistFormSection({ section, accent = "#0B5CFF" }) {
  const s = section || {};
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Derive the page slug from the URL — this component always renders on /p/{slug} or on a custom-domain host.
  // We POST to the backend directly using the API_BASE + slug from the location.
  const slug = (() => {
    const m = window.location.pathname.match(/^\/p\/([^/]+)/);
    if (m) return m[1];
    // On a primary-page custom domain, MarketingHome renders without /p/{slug}.
    // In that flow, we can read a data attribute injected by MarketingHome, or fall back to null which disables the form.
    return window.__vp_active_page_slug || null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    if (!slug) { toast.error("Waitlist can only be submitted from a published page"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/public/pages/${slug}/waitlist`, {
        email: email.trim(),
        name: name.trim() || undefined,
        source: new URLSearchParams(window.location.search).get("utm_source") || undefined,
      });
      // A/B conversion beacon — fire-and-forget
      const abVariant = localStorage.getItem(`vp_ab_${slug}`) || "a";
      axios.post(`${API_BASE}/public/pages/${slug}/track?variant=${abVariant}&event=conversion`).catch(() => {});
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="py-20" style={{ background: accent + "12" }}>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ color: "#0B0D17" }}>{s.title}</h2>
        {s.subtitle && <p className="text-[#4A4D57] text-lg mt-4 leading-relaxed">{s.subtitle}</p>}
        {done ? (
          <div className="mt-8 vp-glass p-6 max-w-md mx-auto">
            <div className="font-display font-bold text-xl">🎉 {s.success_message || "You're on the list!"}</div>
            <p className="text-sm text-[#4A4D57] mt-2">Check your inbox — we just sent a confirmation.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 max-w-md mx-auto space-y-3" data-testid="waitlist-form">
            {s.collect_name && (
              <input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
                className="vp-input" data-testid="waitlist-name" />
            )}
            <input type="email" required placeholder="you@example.com" value={email}
              onChange={(e) => setEmail(e.target.value)} className="vp-input" data-testid="waitlist-email" />
            <button type="submit" disabled={busy || !email.trim()}
              className="vp-btn-primary w-full text-base"
              style={{ background: accent, borderColor: accent }}
              data-testid="waitlist-submit">
              {busy ? "Adding you…" : (s.button || "Notify me")}
            </button>
            <p className="text-xs text-[#747680]">One email when the doors open. No spam, ever.</p>
          </form>
        )}
      </div>
    </section>
  );
}

// ---------- COUNTDOWN ----------
export function CountdownSection({ section, accent = "#0B5CFF" }) {
  const s = section || {};
  const [now, setNow] = useState(Date.now());
  const target = s.target ? new Date(s.target).getTime() : null;
  const remaining = target ? Math.max(0, target - now) : 0;
  const done = target && remaining === 0;
  const d = Math.floor(remaining / 86400000);
  const h = Math.floor((remaining % 86400000) / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const sec = Math.floor((remaining % 60000) / 1000);
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const Cell = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-20 sm:w-24 aspect-square rounded-2xl flex items-center justify-center font-display font-black text-3xl sm:text-4xl bg-white shadow-sm border border-[#E4E6EC]" style={{ color: accent }}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-xs text-[#747680] uppercase tracking-widest mt-2">{label}</div>
    </div>
  );
  return (
    <section className="py-20" style={{ background: accent + "0F" }}>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{s.title || "Countdown"}</h2>
        {!target ? (
          <p className="text-[#4A4D57] mt-4">Set a target date in the editor.</p>
        ) : done ? (
          <p className="font-display font-black text-2xl mt-6" style={{ color: accent }}>🎉 We're live!</p>
        ) : (
          <div className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-6" data-testid="countdown">
            <Cell value={d} label="Days" />
            <Cell value={h} label="Hours" />
            <Cell value={m} label="Minutes" />
            <Cell value={sec} label="Seconds" />
          </div>
        )}
      </div>
    </section>
  );
}


// ---------- REGISTRATION FORM ----------
export function RegistrationFormSection({ section, accent = "#0B5CFF" }) {
  const s = section || {};
  const fields = s.fields || { name: true, mobile: true, email: false, city: true, requirement: false };
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", requirement: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const slug = (() => {
    const m = window.location.pathname.match(/^\/p\/([^/]+)/);
    if (m) return m[1];
    return window.__vp_active_page_slug || null;
  })();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const waNum = String(s.whatsapp_number || "").replace(/[^0-9]/g, "");
  const callNum = String(s.call_number || "").replace(/[^0-9+]/g, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || busy) return;
    if (!slug) { toast.error("Registration can only be submitted from a published page"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        mobile: fields.mobile ? (form.mobile.trim() || null) : null,
        email: fields.email ? (form.email.trim() || null) : null,
        city: fields.city ? (form.city.trim() || null) : null,
        requirement: fields.requirement ? (form.requirement.trim() || null) : null,
        source: new URLSearchParams(window.location.search).get("utm_source") || null,
        section_id: s.id || null,
      };
      await axios.post(`${API_BASE}/public/pages/${slug}/register`, payload);
      // A/B conversion beacon
      try {
        const abVariant = localStorage.getItem(`vp_ab_${slug}`) || "a";
        axios.post(`${API_BASE}/public/pages/${slug}/track?variant=${abVariant}&event=conversion`).catch(() => {});
      } catch (_e) { /* noop */ }
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section id="registration-form" className="py-16 sm:py-20" style={{ background: accent + "10" }}>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ color: "#0B0D17" }}>
          {s.title || "Register now"}
        </h2>
        {s.subtitle && <p className="text-[#4A4D57] text-base sm:text-lg mt-4 leading-relaxed">{s.subtitle}</p>}

        {done ? (
          <div className="mt-8 rounded-2xl bg-white border shadow-sm p-6 sm:p-8" data-testid="registration-thankyou">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-white text-2xl" style={{ background: accent }}>✓</div>
            <div className="font-display font-bold text-2xl mt-4">{s.success_title || "Thank you!"}</div>
            {s.success_message && <p className="text-[#4A4D57] text-sm mt-2 leading-relaxed">{s.success_message}</p>}
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {waNum && (
                <a
                  href={`https://wa.me/${waNum}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-white"
                  style={{ background: "#25D366" }}
                  data-testid="registration-whatsapp-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.59 1.37 5.09L2 22l4.99-1.31A9.94 9.94 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm5.28 14.41c-.24.67-1.4 1.29-1.94 1.34-.53.05-1.05.24-3.55-.74-3.01-1.19-4.94-4.23-5.09-4.43-.15-.2-1.22-1.62-1.22-3.1s.77-2.2 1.05-2.5c.28-.3.62-.37.83-.37s.4 0 .58.01c.19.01.44-.07.69.53.24.6.83 2.08.9 2.23.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.18-.31.4-.44.53-.15.15-.3.31-.13.6.17.3.75 1.24 1.61 2.01 1.1.98 2.03 1.28 2.34 1.43.3.15.48.13.66-.08.18-.2.76-.88.96-1.19.2-.3.4-.25.68-.15.28.1 1.75.82 2.05.97.3.15.5.22.58.35.07.13.07.75-.17 1.42z"/></svg>
                  WhatsApp us
                </a>
              )}
              {callNum && (
                <a
                  href={`tel:${callNum}`}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-white"
                  style={{ background: accent }}
                  data-testid="registration-call-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.4.6 3.5.1.3 0 .7-.2 1l-2.2 2.3z"/></svg>
                  Call now
                </a>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 text-left max-w-md mx-auto space-y-3" data-testid="registration-form">
            {fields.name && (
              <input type="text" required placeholder="Your name" value={form.name}
                onChange={(e) => set("name", e.target.value)} className="vp-input" data-testid="reg-field-name" />
            )}
            {fields.mobile && (
              <input type="tel" required={!fields.email} placeholder="Mobile / WhatsApp number" value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)} className="vp-input" data-testid="reg-field-mobile" />
            )}
            {fields.email && (
              <input type="email" placeholder="Email" value={form.email}
                onChange={(e) => set("email", e.target.value)} className="vp-input" data-testid="reg-field-email" />
            )}
            {fields.city && (
              <input type="text" placeholder="City" value={form.city}
                onChange={(e) => set("city", e.target.value)} className="vp-input" data-testid="reg-field-city" />
            )}
            {fields.requirement && (
              <textarea rows={3} placeholder="Kya requirement hai? (optional)" value={form.requirement}
                onChange={(e) => set("requirement", e.target.value)} className="vp-input" data-testid="reg-field-requirement" />
            )}
            <button type="submit" disabled={busy || !form.name.trim()}
              className="vp-btn-primary w-full text-base"
              style={{ background: accent, borderColor: accent }}
              data-testid="registration-submit">
              {busy ? "Submitting…" : (s.button || "Submit")}
            </button>
            <p className="text-xs text-[#747680] text-center">Aapki details safe hain — sirf hamari team hi dekh sakti hai.</p>
          </form>
        )}
      </div>
    </section>
  );
}
